// Utility functions for managing calendar events in local storage

const BASE_KEY = 'locumloop_calendar_events';

const getStorageKey = (userId) => {
  if (!userId) return `${BASE_KEY}_global`;
  return `${BASE_KEY}_${userId}`;
};

/**
 * Get all events from local storage for a specific user
 * @param {string|number} [userId] - optional user id to namespace storage
 * @returns {Array} Array of event objects
 */
export const getEvents = (userId) => {
  try {
    const key = getStorageKey(userId);
    const eventsJson = localStorage.getItem(key);
    return eventsJson ? JSON.parse(eventsJson) : [];
  } catch (error) {
    console.error('Error reading events from local storage:', error);
    return [];
  }
};

/**
 * Save events to local storage for a specific user
 * @param {Array} events - Array of event objects
 * @param {string|number} [userId] - optional user id to namespace storage
 */
export const saveEvents = (events, userId) => {
  try {
    const key = getStorageKey(userId);
    localStorage.setItem(key, JSON.stringify(events));
  } catch (error) {
    console.error('Error saving events to local storage:', error);
  }
};

/**
 * Add a new event for a specific user
 * @param {Object} event - Event object with title, start, end, etc.
 * @param {string|number} [userId] - optional user id to namespace storage
 * @returns {Object} The added event with generated ID
 */
export const addEvent = (event, userId) => {
  const events = getEvents(userId);
  const newEvent = {
    id: Date.now().toString(),
    ...event,
    createdAt: new Date().toISOString(),
  };
  events.push(newEvent);
  saveEvents(events, userId);
  return newEvent;
};

/**
 * Update an existing event for a specific user
 * @param {string} eventId - ID of the event to update
 * @param {Object} updates - Partial event object with fields to update
 * @param {string|number} [userId] - optional user id to namespace storage
 * @returns {Object|null} The updated event or null if not found
 */
export const updateEvent = (eventId, updates, userId) => {
  const events = getEvents(userId);
  const index = events.findIndex(e => e.id === eventId);
  if (index === -1) return null;
  
  events[index] = {
    ...events[index],
    ...updates,
    updatedAt: new Date().toISOString(),
  };
  saveEvents(events, userId);
  return events[index];
};

/**
 * Delete an event for a specific user
 * @param {string} eventId - ID of the event to delete
 * @param {string|number} [userId] - optional user id to namespace storage
 * @returns {boolean} True if event was deleted, false if not found
 */
export const deleteEvent = (eventId, userId) => {
  const events = getEvents(userId);
  const filteredEvents = events.filter(e => e.id !== eventId);
  if (filteredEvents.length === events.length) return false;

  saveEvents(filteredEvents, userId);
  return true;
};

/**
 * Clear events for a specific user (or global if no userId provided)
 */
export const clearAllEvents = (userId) => {
  const key = getStorageKey(userId);
  localStorage.removeItem(key);
};

